import React, { useContext } from "react";
import {
  AppBar,
  Box,
  IconButton,
  Toolbar,
  Typography,
  useScrollTrigger,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LogoutIcon from "@mui/icons-material/Logout";
import NotificationMenu from "./NotificationMenu";
import { ThemeContext } from "../../utils/ThemeContext";

const MemberTopBar = ({
  userFullName,
  hasPermission,
  notifications,
  notificationsAnchorEl,
  openNotificationsMenu,
  closeNotificationsMenu,
  unreadCount,
  navigate,
  handleUserProfile,
  handleSignOut,
}) => {
  const { theme } = useContext(ThemeContext);
  const [anchorEl, setAnchorEl] = React.useState(null);

  const trigger = useScrollTrigger({ disableHysteresis: true, threshold: 10 });
  const appBarColor = theme?.appBarColor || "#2f63ad";
  const borderColor = "rgba(255,255,255,0.14)";

  const initials = userFullName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0])
    .join("")
    .toUpperCase();

  const iconBg = trigger ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.15)";
  const iconHoverBg = trigger ? "rgba(255,255,255,0.28)" : "rgba(255,255,255,0.25)";

  const iconButtonSx = {
    color: "#fff",
    backgroundColor: iconBg,
    border: `1px solid ${borderColor}`,
    width: 32,
    height: 32,
    transition: "all 0.2s ease",
    "& svg": { fontSize: 18 },
    "&:hover": { backgroundColor: iconHoverBg },
  };

  // Greeting based on time of day
  const hour = new Date().getHours();
  const greeting =
    hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const firstName = userFullName.split(" ")[0] || "there";

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        background: trigger ? `${appBarColor}CC` : appBarColor,
        boxShadow: trigger
          ? "0 6px 16px rgba(0,0,0,0.12)"
          : "0 6px 16px rgba(0,0,0,0.14)",
        borderBottom: `1px solid ${borderColor}`,
        px: { xs: 1, sm: 1.5, md: 2 },
        py: 0.5,
        zIndex: 1100,
        backdropFilter: trigger ? "blur(10px) saturate(140%)" : "none",
        WebkitBackdropFilter: trigger ? "blur(10px) saturate(140%)" : "none",
        transition: "all 0.3s ease",
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          minHeight: "48px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pl: { xs: 1.5, sm: 2, md: 2.5 },
          pr: 0.5,
          gap: 1,
        }}
      >
        {/* Greeting */}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="body1"
            sx={{
              color: "#f4f7fb",
              fontWeight: 700,
              letterSpacing: "0.2px",
              fontSize: { xs: "0.88rem", sm: "0.98rem" },
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {greeting}, {firstName}!
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.72rem" }}
          >
            Welcome to your homeowner portal
          </Typography>
        </Box>

        {/* Right side actions */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0 }}>
          {/* Notifications */}
          {hasPermission("view_notifications") && (
            <>
              <IconButton onClick={openNotificationsMenu} sx={iconButtonSx}>
                <Box sx={{ position: "relative", display: "flex" }}>
                  <NotificationsIcon />
                  {unreadCount > 0 && (
                    <Box
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        backgroundColor: "#ff4d4f",
                        position: "absolute",
                        top: 1,
                        right: 0,
                        border: `1.5px solid ${appBarColor}`,
                      }}
                    />
                  )}
                </Box>
              </IconButton>
              <NotificationMenu
                anchorEl={notificationsAnchorEl}
                notifications={notifications}
                onClose={closeNotificationsMenu}
                navigate={navigate}
              />
            </>
          )}

          {/* Avatar menu */}
          <IconButton
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{ p: 0.5 }}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                backgroundColor: "rgba(255,255,255,0.22)",
                color: "#fff",
                fontWeight: 700,
                fontSize: "0.8rem",
                border: `1.5px solid ${borderColor}`,
                cursor: "pointer",
                transition: "background 0.2s",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.32)" },
              }}
            >
              {initials}
            </Avatar>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            PaperProps={{
              elevation: 0,
              sx: {
                mt: 1,
                borderRadius: "14px",
                border: "1px solid rgba(47,99,173,0.12)",
                boxShadow: "0 12px 36px rgba(0,0,0,0.14)",
                minWidth: 200,
                overflow: "visible",
              },
            }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            {/* Identity header */}
            <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: "#223142" }}>
                {userFullName}
              </Typography>
              <Typography variant="caption" sx={{ color: "#6b778c" }}>
                Homeowner
              </Typography>
            </Box>

            <Divider sx={{ my: 0.5 }} />

            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                handleUserProfile();
              }}
              sx={{ borderRadius: "10px", mx: 0.5, gap: 1.25 }}
            >
              <ListItemIcon sx={{ minWidth: "unset" }}>
                <PersonOutlineIcon fontSize="small" sx={{ color: "#2f63ad" }} />
              </ListItemIcon>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>My Profile</Typography>
            </MenuItem>

            <Divider sx={{ my: 0.5 }} />

            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                handleSignOut();
              }}
              sx={{ borderRadius: "10px", mx: 0.5, gap: 1.25, color: "#dc2626" }}
            >
              <ListItemIcon sx={{ minWidth: "unset" }}>
                <LogoutIcon fontSize="small" sx={{ color: "#dc2626" }} />
              </ListItemIcon>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#dc2626" }}>
                Sign Out
              </Typography>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default MemberTopBar;
